import { Card, Tag } from 'antd';
import { companyColors } from 'apps/investment/constants';
import {
  fundOrderStatusColors,
  fundOrderStatusLabels,
  orderSourceLabels,
  orderTypeColors as fundOrderTypeColors,
  orderTypeLabels as fundOrderTypeLabels,
} from 'apps/investment/constants/fundOrder';
import { modifyCancelColumnsGenerator } from 'apps/investment/components/stock-order/config';
import {
  modifyCancelTypeColors,
  modifyCancelTypeLabels,
  orderSideColors,
  orderSideLabels,
  orderStatusColors,
  orderTypeColors,
  stockOrderAppStatusLabels,
  stockOrderStatusLabels,
} from 'apps/investment/constants/stockOrder';
import { DataTable } from 'common/data-table';
import CashSVG from 'common/svg/cash-svg';
import InfoCard from 'common/ui/info-card';
import { commonDictionary } from 'config/dictionary';
import { History, Info, Receipt } from 'lucide-react';
import { Link } from 'react-router-dom';
import { cn } from 'utils/cn';
import { formatCurrency, formatNumber } from 'utils/formatAmount';
import { formatDate, formatDateTime } from 'utils/formatDate';

const ItemDetail = ({ item }) => {
  const { user, stock } = item || {};

  const getAppNote = () => {
    const matched_volume = item?.matched_volume;
    const volume = item?.volume;

    if (!matched_volume) {
      return '';
    }
    if (matched_volume < volume) {
      return 'Khớp một phần';
    }
    if (matched_volume === volume) {
      return 'Khớp toàn bộ';
    }
  };

  return (
    <div>
      <Card
        className={cn('my-4 shadow-md overflow-hidden border-2', {
          'border-green-500': item.order_side === 'buy',
          'border-blue-500': item.order_side === 'sell',
        })}
      >
        <div className="space-y-4">
          {/* Basic Information Section */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard
              label={'Lệnh'}
              value={
                <Tag color={orderSideColors[item.order_side]}>
                  {orderSideLabels[item.order_side]}
                </Tag>
              }
            />
            <InfoCard
              label={'Trạng thái'}
              value={
                <Tag color={orderStatusColors[item.status]}>
                  {item.status_label ||
                    stockOrderStatusLabels[item.status] ||
                    item.status}
                </Tag>
              }
            />
            <InfoCard
              label={'App status'}
              value={
                <Tag color={orderStatusColors[item.status]}>
                  {stockOrderAppStatusLabels[item.status]}
                </Tag>
              }
            />
            <InfoCard label={'App note'} value={<div>{getAppNote()}</div>} />
            <InfoCard
              label="Order Type"
              value={
                <Tag color={orderTypeColors[item.order_type]}>
                  {item.order_type?.toUpperCase()}
                </Tag>
              }
            />
            <InfoCard
              label="Order Source"
              value={
                <Tag color={companyColors[item.order_source]}>
                  {orderSourceLabels[item.order_source] || item.order_source}
                </Tag>
              }
            />
            <InfoCard
              label={'Sản phẩm'}
              value={
                <Link to={`/admin/products/etfs/${stock?.id}`}>
                  {stock?.symbol}
                </Link>
              }
            />

            <InfoCard
              className="break-words"
              label="User"
              value={
                <Link to={`/investment/users/${user.id}`}>
                  {user.email || user.id}
                </Link>
              }
            />
          </div>

          {/* Timestamps */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <InfoCard
              label={commonDictionary.created_at}
              value={formatDateTime(item.created_at)}
            />
            <InfoCard
              label={commonDictionary.updated_at}
              value={formatDateTime(item.updated_at)}
            />
            <InfoCard
              label="Order Date"
              value={formatDateTime(item.order_date)}
            />
            <InfoCard label="Trade Date" value={formatDate(item.trade_date)} />
          </div>

          {/* Trading Information Section */}
          <h3 className="text-base font-medium text-gray-800 pt-2 border-b flex items-center">
            <CashSVG className="mr-1" />
            Thông tin lệnh
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard label="Price" value={formatCurrency(item.price)} />
            <InfoCard label={'Volume'} value={formatNumber(item.volume)} />
            <InfoCard label="Value" value={formatCurrency(item.value)} />
            <InfoCard
              label="Estimate Amount"
              value={formatCurrency(item.estimate_amount)}
            />

            <InfoCard
              label="Matched Price"
              value={formatCurrency(item.matched_price)}
            />
            <InfoCard
              label="Matched Volume"
              value={formatNumber(item.matched_volume)}
            />
            <InfoCard
              label="Matched Value"
              value={formatCurrency(item.matched_value)}
            />
            <InfoCard label="Amount" value={formatCurrency(item.amount)} />

            <InfoCard
              label="Outstanding Volume"
              value={formatNumber(item.outstanding_volume)}
            />
            <InfoCard label="Fee" value={formatCurrency(item.fee)} />
            <InfoCard label="Tax" value={formatCurrency(item.tax)} />
          </div>

          {/* Security Order Information Section */}
          <h3 className="text-base font-medium text-gray-800 border-b pt-2 flex items-center">
            <Info className="text-primary size-5 mr-1" />
            Thông tin từ hệ thống chứng khoán
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard
              label="Security Order ID"
              value={item.security_order_id}
            />
            <InfoCard
              label="Security Order Status"
              value={item.security_order_status}
            />
            <InfoCard
              label="Security Order Time"
              value={formatDateTime(item.security_order_time)}
            />
            <InfoCard
              label="Security Order Message"
              value={item.security_order_message}
            />
            <InfoCard
              label="Order Modify/Cancel Type"
              value={
                item.order_modify_cancel_type ? (
                  <Tag color={modifyCancelTypeColors[item.order_modify_cancel_type]}>
                    {modifyCancelTypeLabels[item.order_modify_cancel_type] ||
                      item.order_modify_cancel_type}
                  </Tag>
                ) : null
              }
            />
            <InfoCard
              label="Security Original Order ID"
              value={item.security_original_order_id}
            />
          </div>
        </div>
      </Card>

      {/* Modify/Cancel History Section */}
      {item.modify_cancels && item.modify_cancels.length > 0 && (
        <Card className="my-4 shadow-md overflow-hidden border-2">
          <h3 className="text-base font-medium text-gray-800 border-b pb-2 mb-4 flex items-center">
            <History className="size-5 mr-1" />
            Lịch sử sửa / huỷ lệnh
          </h3>
          <DataTable
            columns={modifyCancelColumnsGenerator()}
            data={item.modify_cancels}
            tableName="stockOrderModifyCancels"
            noViewOptions={true}
          />
        </Card>
      )}

      {/* Transaction Section */}
      {item.transaction && (
        <Card className="my-4 shadow-md overflow-hidden border-2 border-blue-400">
          <h3 className="text-base font-medium text-blue-600 border-b border-blue-200 pb-2 mb-4 flex items-center">
            <Receipt className="size-5 mr-1" />
            Thông tin giao dịch (Transaction)
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <InfoCard label="Transaction ID" value={item.transaction.id} />
            <InfoCard label="Trading Account" value={item.transaction.trading_account_code} />
            <InfoCard
              label="Trading Account Manager"
              value={
                <Tag color={companyColors[item.transaction.trading_account_manager]}>
                  {item.transaction.trading_account_manager}
                </Tag>
              }
            />
            <InfoCard label="Date" value={formatDateTime(item.transaction.date)} />
            <InfoCard
              label="Source"
              value={
                <Tag color={companyColors[item.transaction.source]}>
                  {item.transaction.source}
                </Tag>
              }
            />
            <InfoCard
              label="Type"
              value={
                <Tag color={fundOrderTypeColors[item.transaction.type]}>
                  {fundOrderTypeLabels[item.transaction.type] || item.transaction.type}
                </Tag>
              }
            />
            <InfoCard
              label="Status"
              value={
                <Tag color={fundOrderStatusColors[item.transaction.status]}>
                  {fundOrderStatusLabels[item.transaction.status] || item.transaction.status}
                </Tag>
              }
            />
            <InfoCard label="Product Type" value={item.transaction.product_type} />
            <InfoCard label="Product Code" value={item.transaction.product_code} />
            <InfoCard label="Product Alias" value={item.transaction.product_alias} />
            <InfoCard label="Price" value={formatCurrency(item.transaction.price)} />
            <InfoCard label="Quantity" value={formatNumber(item.transaction.quantity)} />
            <InfoCard label="Amount" value={formatCurrency(item.transaction.amount)} />
            <InfoCard label="Net Amount" value={formatCurrency(item.transaction.net_amount)} />
            <InfoCard label="Order Date" value={formatDate(item.transaction.order_date)} />
          </div>
        </Card>
      )}
    </div>
  );
};

export default ItemDetail;
